import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Zap, ChevronRight, Check, Loader2, AlertCircle } from 'lucide-react';

const StepItem = ({ step, isLast, isLoading }) => {
  const [open, setOpen] = useState(false);

  const isThinking = step?.tool_name === 'thinking...';
  const label = isThinking ? 'Thinking' : `${step?.tool_name}`;
  const icon = isThinking ? (
    <Brain className="size-5 text-primary" />
  ) : (
    <Zap className="size-5 text-primary" />
  );

  const statusEl =
    isLast && (isLoading || step?.isLoading) ? (
      <Loader2 className="size-5 animate-spin text-primary" />
    ) : step.status === 'error' ? (
      <AlertCircle className="size-5 text-[#ef4444]" />
    ) : (
      <Check className="size-5 text-primary" />
    );

  const hasContent = isThinking ? !!step?.content : !!(step?.tool_result || step?.tool_args);

  return (
    <div className={`relative flex items-center gap-2.5 ${!isLast && 'mb-[10px]'}`}>
      {/* Timeline */}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <button
          onClick={() => hasContent && setOpen(!open)}
          className={`flex w-full items-center gap-2 text-left group  ${open && 'mb-5'}`}
          disabled={!hasContent}>
          <div className="flex flex-col items-center pt-1">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
              {icon}
            </div>
            {!isLast && <div className="mt-1 flex-1 w-px bg-[#262626]" />}
          </div>
          <span className="text-[16px] leading-5 font-medium text-[#c8c8c8] group-hover:text-white transition-colors">
            {label}
          </span>
          {statusEl}
          {hasContent && (
            <ChevronRight
              className={`ml-auto size-5 text-[#707070] transition-transform duration-200 ${open ? 'rotate-90' : ''}`}
            />
          )}
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden">
              {isThinking ? (
                <div className="rounded-lg bg-[rgba(157,157,157,0.03)] border border-[rgba(157,157,157,0.5)] px-3 py-2 mb-4">
                  <p className="text-sm text-[#a0a0a0] leading-relaxed whitespace-pre-wrap">{step?.content}</p>
                </div>
              ) : (
                <div className="mt-1.5 space-y-1.5 mb-4">
                  {step?.tool_args && (
                    <div className="rounded-lg bg-[rgba(157,157,157,0.03)] border border-[rgba(157,157,157,0.4)] px-3 py-2 pt-4">
                      <p className="text-sm font-semibold uppercase tracking-wider text-[#707070] mb-1">
                        Input
                      </p>
                      <pre className="!bg-transparent text-xs font-mono text-[#a0a0a0] leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto !border-none !p-0">
                        {JSON.stringify(step?.tool_args, null, 2)}
                      </pre>
                    </div>
                  )}

                  {step?.tool_result && (
                    <div className="rounded-lg bg-[rgba(157,157,157,0.03)] border border-[rgba(157,157,157,0.4)] px-3 py-2 pt-4">
                      <p className="text-sm font-semibold uppercase tracking-wider text-[#707070] mb-1">
                        Output
                      </p>
                      <pre className="!bg-transparent text-xs font-mono text-[#a0a0a0] leading-relaxed whitespace-pre-wrap max-h-32 overflow-y-auto !border-none !p-0">
                        {step?.tool_result}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StepItem;
